import React from 'react';
import get from 'lodash.get';
import intersection from 'lodash.intersection';
import { useSelector } from 'react-redux';

import { CustomsForm } from './components/customs-form';
import { Label } from './components/label';
import { OrderSummary } from './components/order-summary';
import { PackingSlip } from './components/packing-slip';
import { PickList } from './components/pick-list';
import { useOrderSummary } from './hooks/use-order-summary';
import { usePackingSlipDisabledCheck } from './hooks/use-packing-slip';
import { usePickListDisabledCheck } from './hooks/use-pick-list';
import PrintOrderSummaryHotkey from '../../../components/hotkeys/advanced-hotkeys/print-order-summary';
import PrintPackingSlipHotkey from '../../../components/hotkeys/advanced-hotkeys/print-packing-slip';
import PrintPickListHotkey from '../../../components/hotkeys/advanced-hotkeys/print-pick-list';
import PrintDocumentButton from '../../../components/print-document-button';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { DocumentType } from '../../../enums/document-type';
import { useEnablePickListHtmlEditor } from '../../../feature-flags/hooks/use-enable-pick-list-html-editor';
import { usePickListsCustomizedFeatureGate } from '../../../feature-gates/use-picklists-customized-feature-gate';
import { getIsBatchDetailsPage } from '../../../store/batches/batches.helpers';
import Global from '../../../store/global-selectors';
import { getActualSalesOrderIds, getActualFulfillmentPlanIds } from '../../../store/orders/orders.helpers';

/**
 * @param {object} props
 * @param {string} props.dropdownTogglerClassname
 * @param {string} props.theme
 * @param {boolean} props.unstyled - Overrides built-in styling specific to the print button
 * @returns {JSX.Element}
 */
export const OrdersGridPrintDocumentButton = ({ dropdownTogglerClassname, theme = 'tertiary', unstyled }) => {
  const { isFeatureEnabled: isPickListsCustomizedEnable } = usePickListsCustomizedFeatureGate();
  const isPickListHtmlEditorEnabled = useEnablePickListHtmlEditor();
  const userHasShippingRole = useSelector(Global.selectUserhasShippingRole);
  const selectedEntityIds = useSelector(Global.selectSelectedOrdersIds);
  const orderGridIds = useSelector(Global.selectOrdersGridOrders);
  const sortedSelectedEntityIds = intersection(orderGridIds, selectedEntityIds);
  const salesOrderIds = useSelector((state) => getActualSalesOrderIds(state, sortedSelectedEntityIds));
  const fulfillmentPlanIds = useSelector((state) => getActualFulfillmentPlanIds(state, sortedSelectedEntityIds));
  const salesOrders = useSelector((state) => Global.selectEntitiesSalesOrdersByIds(state, { salesOrderIds })) || emptyArray;
  const fulfillmentPlans = useSelector((state) => Global.selectEntitiesFulfillmentPlansByIds(state, { fulfillmentPlanIds })) || emptyArray;
  const fulfillmentIds = fulfillmentPlans.reduce((acc, order) => {
    const newFulfillmentIds = get(order, 'fulfillmentIds', emptyArray);
    acc.push(...newFulfillmentIds);
    return acc;
  }, []);
  const fulfillments = useSelector((state) => Global.selectEntitiesFulfillmentsByIds(state, { fulfillmentIds })) || emptyArray;
  const isOnBatchesGrid = getIsBatchDetailsPage();
  const printSettings = useSelector(Global.selectPrintSettingsPrintSettings) || emptyObject;
  const shouldBeDisabled = !isOnBatchesGrid && !selectedEntityIds.length;
  const { isDisabled: orderSummaryIsDisabled, onPrint: orderSummaryHotkeyHandler } = useOrderSummary({ fulfillmentPlans });
  const { isDisabled: packingSlipIsDisabled, onClick: packingSlipHotkeyHandler } = usePackingSlipDisabledCheck({
    fulfillmentPlans,
    salesOrders,
  });
  const { isDisabled: pickListIsDisabled, onClick: pickListHotkeyHandler } = usePickListDisabledCheck({ fulfillmentPlans, salesOrders });

  return (
    <React.Fragment>
      {!orderSummaryIsDisabled && <PrintOrderSummaryHotkey handler={orderSummaryHotkeyHandler} />}
      {!packingSlipIsDisabled && <PrintPackingSlipHotkey handler={packingSlipHotkeyHandler} />}
      {!pickListIsDisabled && <PrintPickListHotkey handler={pickListHotkeyHandler} />}
      <PrintDocumentButton
        disabled={shouldBeDisabled}
        dropdownTogglerClassname={dropdownTogglerClassname}
        theme={theme}
        unstyled={unstyled}
      >
        <PackingSlip
          fulfillmentPlans={fulfillmentPlans}
          printSetting={printSettings?.[DocumentType.PackingSlip.printSettingKey]}
          salesOrders={salesOrders}
          userHasShippingRole={userHasShippingRole}
        />
        <Label
          fulfillments={fulfillments}
          printSetting={printSettings?.[DocumentType.Label.printSettingKey]}
          userHasShippingRole={userHasShippingRole}
        />
        <PickList
          fulfillmentPlans={fulfillmentPlans}
          printSetting={printSettings?.[DocumentType.PickList.printSettingKey]}
          salesOrders={salesOrders}
        />
        {isPickListsCustomizedEnable && !isPickListHtmlEditorEnabled ? (
          <PickList
            fulfillmentPlans={fulfillmentPlans}
            isUseCustomerDocType
            printSetting={printSettings?.[DocumentType.PickList.printSettingKey]}
            salesOrders={salesOrders}
          />
        ) : null}
        <CustomsForm
          fulfillmentPlans={fulfillmentPlans}
          fulfillments={fulfillments}
          printSetting={printSettings?.[DocumentType.CustomsForm.printSettingKey]}
          salesOrders={salesOrders}
          userHasShippingRole={userHasShippingRole}
        />
        <OrderSummary fulfillmentPlans={fulfillmentPlans} printSetting={printSettings?.[DocumentType.OrderSummary.printSettingKey]} />
      </PrintDocumentButton>
    </React.Fragment>
  );
};
